import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { 
  Users, MapPin, Calendar, Clock, FileText, Eye, 
  MessageCircle, Target, Award, CheckCircle, XCircle 
} from 'lucide-react';

const TrialApplicationDetailModal = ({ 
  isOpen, 
  onClose, 
  application, 
  onStatusUpdate, 
  onNavigate 
}) => {
  if (!application) return null;

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'applied': 'badge-primary',
      'shortlisted': 'badge-warning',
      'accepted': 'badge-success',
      'rejected': 'badge-danger'
    };
    return statusMap[status] || 'badge-neutral';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto mx-4 p-0" data-testid="trial-application-detail-modal">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary/10 to-purple-400/10 p-6 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <div className="icon-container-blue">
                <Users className="w-5 h-5 text-primary" />
              </div>
              Trial Application Details
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-6">
          {/* Athlete Info Card */}
          <div className="card-premium p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background">
                <AvatarImage src={application.athlete?.profileImage} alt={application.athlete?.name} />
                <AvatarFallback className="text-lg bg-primary/10 text-primary font-semibold">
                  {application.athlete?.name?.[0] || 'A'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{application.athlete?.name || 'Unknown Athlete'}</h3>
                    <p className="text-sm text-muted-foreground">@{application.athlete?.username || 'unknown'}</p>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      {application.athlete?.sport && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                          🏆 {application.athlete.sport}
                        </span>
                      )}
                      {application.athlete?.location && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                          <MapPin className="w-3 h-3" /> {application.athlete.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={getStatusBadgeClass(application.status)}>
                    {application.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Trial Info */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              Trial Applied For
            </h4>
            <div className="bg-accent/30 rounded-xl p-4">
              <h5 className="font-bold text-lg text-foreground">{application.trialTitle}</h5>
              {application.trialDate && (
                <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(application.trialDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>

          {/* Application Message */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-primary" />
              Application Message
            </h4>
            {application.message ? (
              <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{application.message}</p>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground">No message provided</p>
              </div>
            )}
          </div>

          {/* Attachments */}
          {(application.cvUrl || application.videoUrl) && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                Attachments
              </h4>
              <div className="flex flex-wrap gap-3">
                {application.cvUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(application.cvUrl, '_blank')}
                    className="hover-lift"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> View CV/Resume
                  </Button>
                )}
                {application.videoUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(application.videoUrl, '_blank')}
                    className="hover-lift"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> View Video
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Athlete Stats */}
          {application.athlete?.stats && Object.keys(application.athlete.stats).length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Award className="w-4 h-4 text-primary" />
                Athlete Stats
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(application.athlete.stats).slice(0, 6).map(([key, value]) => (
                  <div key={key} className="stat-card text-center">
                    <p className="text-lg font-bold text-foreground">
                      {typeof value === 'object' ? (Array.isArray(value) ? value.length : JSON.stringify(value)) : value}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Application Date */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Applied: {application.appliedAt ? new Date(application.appliedAt).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'Unknown date'}</span>
          </div>

          {/* Action Buttons */}
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-semibold mb-3">Update Status</h4>
            <div className="flex flex-wrap gap-2">
              {application.status !== 'shortlisted' && application.status !== 'accepted' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-purple-400 border-purple-400/30 hover:bg-purple-400/10"
                  onClick={() => onStatusUpdate(application._id, 'shortlisted')}
                >
                  <Target className="w-3.5 h-3.5 mr-1.5" /> Shortlist
                </Button>
              )}
              {application.status !== 'accepted' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-primary border-primary/30 hover:bg-primary/10"
                  onClick={() => onStatusUpdate(application._id, 'accepted')}
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Accept
                </Button>
              )}
              {application.status !== 'rejected' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={() => onStatusUpdate(application._id, 'rejected')}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onNavigate(`/profile/${application.athlete?.username || application.athlete?._id}`);
                  onClose();
                }}
              >
                <Users className="w-3.5 h-3.5 mr-1.5" /> View Profile
              </Button>
              <Button
                size="sm"
                className="btn-primary"
                onClick={() => {
                  onNavigate(`/messages?userId=${application.athlete?._id}`);
                  onClose();
                }}
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Message
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={() => onClose(false)} className="w-full sm:w-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrialApplicationDetailModal;
